import { Box, CircularProgress, Stack, Typography } from '@mui/material';

const BRANCHES = [
  { bg: '#FFF0A8', border: '#E8B83E', icon: '💡', tilt: '-1deg' },
  { bg: '#CDEEFF', border: '#63B5DD', icon: '💬', tilt: '1deg' },
  { bg: '#FFD6E5', border: '#E889A8', icon: '🌸', tilt: '-0.5deg' },
  { bg: '#DDF4C7', border: '#79B45A', icon: '🌱', tilt: '0.8deg' },
  { bg: '#E8D9FF', border: '#9A76D1', icon: '⭐', tilt: '-0.8deg' },
];

const KIND_ICONS = {
  event: '🎬', person: '👥', place: '📍', feeling: '💛', idea: '✨', detail: '🔎', topic: '💡',
};

function IdeaBranch({ node, index }) {
  const style = BRANCHES[index % BRANCHES.length];
  const details = Array.isArray(node?.children) ? node.children.slice(0, 3) : [];
  return (
    <Box
      sx={{
        p: 1.5,
        minHeight: 104,
        border: `3px solid ${style.border}`,
        borderRadius: index % 2
          ? '38% 62% 48% 52% / 55% 45% 55% 45%'
          : '58% 42% 55% 45% / 44% 58% 42% 56%',
        bgcolor: style.bg,
        boxShadow: `0 5px 0 ${style.border}55`,
        transform: `rotate(${style.tilt})`,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Typography aria-hidden sx={{ fontSize: 25, lineHeight: 1, mb: 0.5 }}>
        {KIND_ICONS[node?.kind] || style.icon}
      </Typography>
      <Typography sx={{ fontWeight: 900, color: '#27352C', fontSize: 16, lineHeight: 1.2 }}>
        {node?.label || 'Idea'}
      </Typography>
      {details.length > 0 ? (
        <Stack direction="row" justifyContent="center" useFlexGap flexWrap="wrap" gap={0.6} sx={{ mt: 1 }}>
          {details.map((detail, detailIndex) => (
            <Box
              key={detail.id || detailIndex}
              component="span"
              sx={{
                px: 1,
                py: 0.35,
                borderRadius: 10,
                bgcolor: 'rgba(255,255,255,.82)',
                color: '#34443A',
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {detail.label}
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

export default function MindMapPanel({ document, loading = false, error = '', photoUrl = '', language = 'English' }) {
  if (loading && !document?.root) {
    return <Box sx={{ py: 5, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>;
  }
  if (error && !document?.root) {
    return <Typography role="alert" sx={{ p: 2, color: '#9B1C1C' }}>{error}</Typography>;
  }
  if (!document?.root) return null;
  const branches = Array.isArray(document.root.children)
    ? document.root.children.slice(0, 5)
    : [];
  const centerLabel = {
    English: 'My Story Ideas',
    French: 'Mes idées',
    'Simplified Chinese': '我的故事想法',
    'Traditional Chinese': '我的故事想法',
  }[language] || 'My Story Ideas';
  return (
    <Box
      role="region"
      aria-label={document.title || 'My story ideas'}
      sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2, bgcolor: '#FFFDF3', borderRadius: 4 }}
    >
      <Stack alignItems="center" spacing={1.5}>
        {error && document?.root ? (
          <Typography role="status" sx={{ fontSize: 12, color: '#8A5A20', fontWeight: 700 }}>
            Couldn’t add the newest idea yet. Your map is still here.
          </Typography>
        ) : null}
        {loading ? (
          <Stack direction="row" spacing={0.7} alignItems="center" sx={{ color: '#587261' }}>
            <CircularProgress size={14} color="inherit" />
            <Typography sx={{ fontSize: 12, fontWeight: 800 }}>Growing new ideas…</Typography>
          </Stack>
        ) : null}
        <Box
          sx={{
            minWidth: 170,
            p: 1.3,
            borderRadius: '50%',
            bgcolor: '#DDF5E8',
            border: '4px solid #78B99E',
            boxShadow: '0 6px 0 #B8DDC8',
            textAlign: 'center',
          }}
        >
          {photoUrl ? (
            <Box component="img" src={photoUrl} alt="" sx={{ width: 66, height: 66, objectFit: 'cover', borderRadius: '50%', border: '4px solid #fff' }} />
          ) : <Typography aria-hidden sx={{ fontSize: 38 }}>🌟</Typography>}
          <Typography sx={{ fontWeight: 900, color: '#29463A', fontSize: 17 }}>
            {centerLabel}
          </Typography>
        </Box>

        <Typography aria-hidden sx={{ color: '#8AB59D', letterSpacing: 8, lineHeight: 1 }}>• • •</Typography>

        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.3,
          }}
        >
          {branches.map((branch, index) => (
            <IdeaBranch key={branch.id || index} node={branch} index={index} />
          ))}
        </Box>
      </Stack>
    </Box>
  );
}
